import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import setAnimationTimeout from '../../../core/utils/setAnimationTimeout';
import ServicesMenuChapterI, { ChapterSection } from "./ServicesMenuChapter.interface";
import SectionLayout from "./SectionLayout";
import useOpenSection from "../../helpers/use-open-section";
import { get, isNumber } from "lodash";
import { secToMs } from "../../../core/utils/transforms";
import { NIKE_EASE } from '../../../rise/constants/nikerise';
import { DEFAULT_CAROUSEL_ITEM_DURATION_S, DEFAULT_TRANSITION_DURATION } from './ServiceMenuChapter.constants';

export default function ServicesMenuCarouselScene(props: ServicesMenuChapterI) {
    const {
        sections: rawSections = [],
        is_show_date,
        subtitle,
    } = props;

    const openSection = useOpenSection();
    const [activeIndex, setActiveIndex] = useState(0);
    const cache = useRef([]);

    useEffect(() => {
        if (isNumber(openSection)) {
            setActiveIndex(openSection)
        }
    }, [openSection]);

    useEffect(() => {
        if (isNumber(openSection)) {
            return;
        }

        const delayMs = secToMs(calculateSectionDurationS(sections[activeIndex]));
        return setAnimationTimeout(() => setActiveIndex(i => ++i), delayMs);
    }, [activeIndex, openSection]);

    const sections = useMemo(() => rawSections.filter(item => item), [rawSections]);

    const sectionHeaderSettings = useMemo(() => {
        return {
            isShowDate: is_show_date,
            title: subtitle
        }
    }, [is_show_date, subtitle])

    const isEntrySection = useMemo(() => activeIndex === 0, [activeIndex])

    const activeSectionComponent = useMemo(() => {
        let cachedItem = cache.current[activeIndex];
        if (cachedItem) {
            return cachedItem;
        }

        const section = sections[activeIndex];

        cachedItem = section ? (
            <SectionLayout
                key={activeIndex}
                section={section}
                headerSettings={sectionHeaderSettings}
                isEntrySection={isEntrySection}
            />
        ) : null;

        cache.current.push[cachedItem];

        return cachedItem;
    }, [activeIndex, isEntrySection]);

    return (
        <AnimatePresence>
            <motion.div
                key={activeIndex}
                style={{
                    position: 'absolute',
                    width: '100vw',
                    height: '100vh',
                }}
                initial={isEntrySection ? { y: 0 } : { y: '100vh' }}
                animate={{ x: 0, y: 0 }}
                transition={{
                    duration: DEFAULT_TRANSITION_DURATION,
                    ease: NIKE_EASE
                }}
            >
                {activeSectionComponent}
            </motion.div>
        </AnimatePresence>
    );
}

function calculateSectionDurationS(section: ChapterSection) {
    if (!section) {
        return 0;
    }

    let duration = DEFAULT_CAROUSEL_ITEM_DURATION_S;
    if (section?.has_qr_code) {
        const qrDuration = get(section, 'qr_code.media.force_duration', DEFAULT_CAROUSEL_ITEM_DURATION_S);
        duration += Number(qrDuration);
    }

    return duration;
}

export function calculateSectionsDuration(props: ServicesMenuChapterI, lockedSectionIdx?: number) {
    if (isNumber(lockedSectionIdx)) {
        return calculateSectionDurationS(props.sections[lockedSectionIdx])
    }

    return props.sections.reduce((result, section) => {
        result += calculateSectionDurationS(section);
        return result;
    }, 0);
}
